// Priority values + badge classes are single-sourced from
// shared/config/sf_priorities.config.json via the generated config-enums
// binding. Re-exported so existing consumers keep importing from constants.
// IMPORTED rather than re-exported straight through: `vitest.barrelGraph.ts` narrows an
// IMPORT of the generated barrel to the concrete enum modules and cannot narrow an
// `export … from`, which would give this module — and every test that reaches it — an
// edge to all 440 of them (WI c785985f).
import {
  WORK_ITEM_PRIORITY_VALUES,
  WORK_ITEM_TYPE_VALUES,
  workItemPriorityBadgeClass,
  workItemTypeBadgeClass,
} from '../../generated/config-enums';

export {
  WORK_ITEM_PRIORITY_VALUES as PRIORITY_VALUES,
  workItemPriorityBadgeClass as priorityColors,
};

/**
 * Effort size a new work item starts at, the way it starts at P2 on priority —
 * an estimate a triager adjusts, not a claim. Leaving it unset is worse than a
 * middling guess: an unsized item sorts and reports as if it were free.
 *
 * The vocabulary is tenant-configurable (`sf_tenant_settings.work_item_sizes`),
 * so this dialog applies the default only once that list has loaded AND carries
 * it — the size Select renders its placeholder for a value outside the list, so
 * writing it blind would show "-" and submit "M".
 *
 * It is a display default, not the authority: `DEFAULT_WORK_ITEM_SIZE` in
 * modules/software_factory/work_item_size.py is what actually sizes an item, at
 * `WorkItemService.create_work_item`, for every surface including this one. The
 * literal is spelled twice — as `priority`'s P2 already is — so the operator can
 * SEE and change the estimate before pressing Create.
 */
export const DEFAULT_WORK_ITEM_SIZE = 'M';

/** Initiative RAG health values (matches sf_initiative_health.config.json). */
export const INITIATIVE_HEALTH_VALUES = ['on_track', 'at_risk', 'off_track'] as const;

/** Milestone lifecycle values (matches sf_milestone_statuses.config.json). */
export const MILESTONE_STATUS_VALUES = ['upcoming', 'hit', 'missed'] as const;

/** Semantic StatusChip tone per milestone status (matches the config `tone`). */
export const milestoneStatusTones: Record<string, 'info' | 'success' | 'error' | 'neutral'> = {
  upcoming: 'info',
  hit: 'success',
  missed: 'error',
};

// ── Roadmap horizons (Now / Next / Later) ──────────────────────────────────
// The sequence-not-dates roadmap bucket on an initiative. 'none' = unscheduled.
export type RoadmapHorizon = 'now' | 'next' | 'later' | 'none';

// The three planned lanes, in display order. 'none' is rendered as a separate
// "Unscheduled" backlog lane, not part of the committed sequence.
export const ROADMAP_LANES: readonly RoadmapHorizon[] = ['now', 'next', 'later'] as const;

// Lanes shown on the board, left to right (unscheduled backlog last).
export const ROADMAP_BOARD_LANES: readonly RoadmapHorizon[] = ['now', 'next', 'later', 'none'] as const;

// Accent color per lane (CSS variable color string consumed by KanbanColumn's
// colorDot/borderColor). Dark-mode aware because the vars flip in globals.css.
export const roadmapHorizonColors: Record<RoadmapHorizon, string> = {
  now: 'hsl(var(--info))',
  next: 'hsl(var(--warning))',
  later: 'hsl(var(--muted-foreground))',
  none: 'hsl(var(--border))',
};

/** Work item lifecycle statuses, in workflow order (matches the backend CHECK). */
export const WORK_ITEM_STATUS_VALUES = [
  'new',
  'triaged',
  'planned',
  'in_progress',
  'shipped',
  'wont_do',
] as const;

/**
 * Terminal work-item statuses (design §1b carryover rule). On sprint completion
 * these items stay on the completing sprint; everything else carries over to the
 * next sprint (or the backlog). Mirrors the backend's
 * `_TERMINAL_WORK_ITEM_STATUSES`.
 */
export const TERMINAL_WORK_ITEM_STATUSES = ['shipped', 'wont_do'] as const;

// The `workItemStatusColors` re-export is gone: a work-item status chip renders
// through `components/WorkItemStatusChip`, which reads the generated
// `workItemStatusTone`. See the note in `utils/statusStyles.ts`.

/** Accent color (CSS var string) per status — for KanbanColumn colorDot/borderColor. */
export const workItemStatusAccent: Record<string, string> = {
  new: 'hsl(var(--muted-foreground))',
  triaged: 'hsl(var(--info))',
  planned: 'hsl(var(--info))',
  in_progress: 'hsl(var(--warning))',
  shipped: 'hsl(var(--success))',
  wont_do: 'hsl(var(--destructive))',
};

/**
 * Badge classes per work item type — single-sourced from
 * shared/config/sf_item_types.config.json (the `badge_class` field) via the
 * generated config-enums binding. Re-exported here so existing consumers keep
 * importing `workItemTypeColors` / `WORK_ITEM_TYPE_VALUES` from constants.
 */
export {
  WORK_ITEM_TYPE_VALUES,
  workItemTypeBadgeClass as workItemTypeColors,
};
export type { WorkItemType } from '../../generated/config-enums';

/** Sprint lifecycle values (matches sf_sprints.status CHECK). */
export const SPRINT_STATUS_VALUES = ['planning', 'active', 'completed'] as const;

/** Semantic StatusChip tone per sprint status. */
export const sprintStatusTones: Record<string, 'info' | 'success' | 'neutral'> = {
  planning: 'info',
  active: 'success',
  completed: 'neutral',
};

/**
 * `sf_work_items.title` is VARCHAR(500) and `POST /work-items` takes a raw dict,
 * so nothing between an input and Postgres enforces the bound on its own — an
 * over-length title used to surface as an unexplained 500. Lives here rather
 * than in the create dialog because the surfaces that PRE-FILL a title have to
 * fit the same budget: the sysadmin-alert dialog prefixes `[Sys alert] ` onto an
 * alert title that is itself capped at 500 by cloudwatch_bridge._build_title.
 * Matches `maxLength` in shared/schemas/software_factory/work_item_create.schema.json.
 */
export const WORK_ITEM_TITLE_MAX_LENGTH = 500;
